import React, { FC, useMemo, Fragment, useCallback } from 'react'
import { useTheme } from '@mui/styles'

import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Typography from '@mui/material/Typography'

import ClickLink from '../../components/widgets/ClickLink'
import SectionHeader from '../widgets/SectionHeader'

import icons from '../../icons'

import {
  getPathBreadcrumbs,
  StorageFile,
} from '@kaidam/fc-shared/src/utils/storage'

import {
  IField_Filestore_Type,
} from '@kaidam/fc-shared/src/types/storage'

import {
  FILESTORE_TYPE_PRIVATE,
  FILESTORE_TYPE_PUBLIC,
  FILESTORE_TYPE_SHARED,
  FILESTORE_TYPE_DRIVE_PUBLIC,
  FILESTORE_TITLES,
} from '@kaidam/fc-shared/src/constants'

interface FilestoreLayoutProps {
  section: IField_Filestore_Type,
  path: string,
  showQuickUpload?: boolean,
  onOpenSection: {
    (section: IField_Filestore_Type): void,
  },
  onSetPath: {
    (path: string): void,
  },
}

const FilestoreLayout: FC<React.PropsWithChildren<FilestoreLayoutProps>> = ({
  section,
  path,
  showQuickUpload = false,
  onOpenSection,
  onSetPath,
  children,
}) => {

  const theme = useTheme()

  const onViewFolder = useCallback((path: any) => {
    onSetPath(path)
  }, [
    path,
  ])

  const breadcrumbs = useMemo(() => {
    return getPathBreadcrumbs(path)
  }, [
    path,
  ])
 
  return (
    <Container
      maxWidth="xl"
      disableGutters
      sx={{
        px: 1,
      }}
    >
      <SectionHeader
        title={
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <Breadcrumbs aria-label="breadcrumb">
              <Typography variant="h4">
                <ClickLink
                  onClick={ () => onViewFolder('') }
                >
                  {
                    FILESTORE_TITLES[section]
                  }
                </ClickLink>
              </Typography>
              {
                breadcrumbs.map((breadcrumb, i: number) => {
                  return (
                    <Fragment key={ i }>
                      <Typography variant="h4">
                        <ClickLink
                          onClick={ () => onViewFolder(breadcrumb.path) }
                        >
                          { breadcrumb.name }
                        </ClickLink>
                      </Typography>
                    </Fragment>
                  )
                })
              }
            </Breadcrumbs>
          </Box>
        }
        titleVariant='h4'
        icon='filestore'
        justify='flex-end'
      >
        <ButtonGroup>
          <Button
            aria-selected={ section == FILESTORE_TYPE_PRIVATE }
            onClick={ () => onOpenSection(FILESTORE_TYPE_PRIVATE) }
            variant={ section == FILESTORE_TYPE_PRIVATE ? 'contained' : 'outlined'}
            sx={{
              ml: 1,
              py: 1,
              pl: 3,
            }}
          >
            { FILESTORE_TITLES[FILESTORE_TYPE_PRIVATE] }
          </Button>
          <Button
            aria-selected={ section == FILESTORE_TYPE_SHARED }
            onClick={ () => onOpenSection(FILESTORE_TYPE_SHARED) }
            variant={ section == FILESTORE_TYPE_SHARED ? 'contained' : 'outlined'}
            sx={{
              ml: 1,
              py: 1,
              pl: 3,
            }}
          >
            { FILESTORE_TITLES[FILESTORE_TYPE_SHARED] }
          </Button>
          <Box
            component={ Button }
            aria-selected={ section == FILESTORE_TYPE_PUBLIC }
            onClick={ () => onOpenSection(FILESTORE_TYPE_PUBLIC) }
            variant={ section == FILESTORE_TYPE_PUBLIC ? 'contained' : 'outlined'}
            sx={{
              ml: 1,
              py: 1,
              pl: 3,
            }}
          >
            { FILESTORE_TITLES[FILESTORE_TYPE_PUBLIC] }
          </Box>
        </ButtonGroup>
      </SectionHeader>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'row',
          pt: 2,
        }}
      >
        <Box
          className='content'
          sx={{
            flexGrow: 1,
            overflowY: 'visible',
          }}
        >
          { children }
        </Box>
      </Box>
    </Container>
  )
}

export default FilestoreLayout
